import { acceptHMRUpdate, defineStore } from 'pinia';
import { ref } from 'vue';
import { licenseGiftsService, sponsorRefusal, type SponsorRefusal } from '~/services/license-gifts-service';
import type {
    LicenseGiftClaim,
    LicenseGiftInvitation,
    LicenseGiftInvitationInput,
    LicenseGiftsOverview,
} from '~/types/person/license-gifts';
import { ApiError } from '~/utils/errors';
import { reportFailure } from '~/utils/report-failure';

/** Refusals of what the member sent or who they are, which leave everything the overview holds as it was. */
const REFUSALS_OF_THE_INPUT: ReadonlySet<SponsorRefusal> = new Set(['self_invite', 'simulated_operations', 'first_name_required']);

/**
 * Whether a refused write leaves the held overview in doubt. A refusal of the input (a field the API
 * rejected, the member's own address, a sandbox account, an account with no first name) or a wait
 * the API asked for changed nothing it holds. Any other failure may: the refusal says the overview
 * moved (no licenses left, an invitation no longer waiting), or the request went unanswered and may
 * have landed.
 */
function leavesOverviewInDoubt(error: unknown): boolean {
    if (!(error instanceof ApiError)) return true;
    if (error.code === 429 || Object.keys(error.errors).length > 0) return false;

    const refusal = sponsorRefusal(error);

    return refusal === null || !REFUSALS_OF_THE_INPUT.has(refusal);
}

/**
 * The signed-in member's free licenses, held once for the sidebar's entry and the Free licenses page.
 *
 * Every route answers for whoever is signed in, never for the person on screen, so nothing held here
 * is ever shown under another person's name: the sidebar hides its entry and the page reads nothing
 * while the viewer has someone else's account open.
 *
 * Only the API knows the overview after a write: an invitation answers with itself but not with the
 * licenses left, a resend answers with nothing, and the dates a resend sets are the server's to mint.
 * So a landed write is followed by one read of the overview, and `load` makes sure that read starts
 * after the write landed. The share link is the exception: turning it on answers with the link, and
 * turning it off leaves none, which is the whole of what changed.
 */
export const useFreeLicensesStore = defineStore('free-licenses', () => {
    const overview = ref<LicenseGiftsOverview | null>(null);
    /** When the overview held arrived (epoch ms); null while none is held. */
    const loadedAt = ref<number | null>(null);
    /** The last read did not finish, so an overview still held may be out of date. */
    const loadFailed = ref(false);
    /** A read is out. */
    const loading = ref(false);

    let reading: Promise<void> | null = null;
    // Set when a read is asked for while one is out: that one may have been answered before the
    // write that asked for this one landed, so its answer is dropped unheld and another read follows.
    let askedAgain = false;

    /** Read the member's free licenses. A reader asking while a read is out is answered by a read that starts after it asked. */
    function load(): Promise<void> {
        if (reading) {
            askedAgain = true;
            return reading;
        }

        reading = (async () => {
            loading.value = true;
            try {
                do {
                    askedAgain = false;
                    const answer = await licenseGiftsService.overview();
                    if (!askedAgain) {
                        overview.value = answer;
                        loadedAt.value = Date.now();
                    }
                } while (askedAgain);
                loadFailed.value = false;
            } catch (error: unknown) {
                loadFailed.value = true;
                reportFailure('Reading free licenses', error);
            } finally {
                loading.value = false;
                reading = null;
            }
        })();

        return reading;
    }

    /**
     * The overview, read once: the sidebar's entry and count need an answer, not a fresh one, so a read
     * already out answers them. Until one lands, every ask reads again.
     */
    function ensureLoaded(): Promise<void> {
        if (overview.value) return Promise.resolve();

        return reading ?? load();
    }

    /** Run a write, then bring the overview up to what it changed; a refusal that leaves it in doubt re-reads it too. */
    async function write<T>(send: () => Promise<T>): Promise<T> {
        let answer: T;
        try {
            answer = await send();
        } catch (error: unknown) {
            if (leavesOverviewInDoubt(error)) void load();
            throw error;
        }

        await load();

        return answer;
    }

    /** Email an invitation; resolves once the overview holds it. */
    function invite(input: LicenseGiftInvitationInput): Promise<LicenseGiftInvitation> {
        return write(() => licenseGiftsService.invite(input));
    }

    /**
     * Send a waiting invitation again. Resolves with it as the read that follows holds it, with the
     * expiry the server moved on; null when that read failed or it is no longer waiting.
     */
    async function resend(gift: number): Promise<LicenseGiftInvitation | null> {
        await write(() => licenseGiftsService.resend(gift));

        return loadFailed.value ? null : (overview.value?.invitations.find(({ id }) => id === gift) ?? null);
    }

    async function openShareLink(): Promise<void> {
        try {
            const link = await licenseGiftsService.openShareLink();
            if (overview.value) overview.value = { ...overview.value, share_link: link };
        } catch (error: unknown) {
            if (leavesOverviewInDoubt(error)) void load();
            throw error;
        }
    }

    async function closeShareLink(): Promise<void> {
        try {
            await licenseGiftsService.closeShareLink();
            if (overview.value) overview.value = { ...overview.value, share_link: null };
        } catch (error: unknown) {
            if (leavesOverviewInDoubt(error)) void load();
            throw error;
        }
    }

    /**
     * Accept a gift as the signed-in member. The claim changes who covers them and answers with the
     * seat alone, so the overview held from before it is dropped, and its next reader loads it afresh.
     */
    async function claim(token: string, allowManagement: boolean): Promise<LicenseGiftClaim> {
        const claimed = await licenseGiftsService.claim(token, allowManagement);
        overview.value = null;
        loadedAt.value = null;
        // A read out now may have been answered before the claim landed.
        if (reading) askedAgain = true;

        return claimed;
    }

    return {
        overview,
        loadedAt,
        loadFailed,
        loading,
        load,
        ensureLoaded,
        invite,
        resend,
        openShareLink,
        closeShareLink,
        claim,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFreeLicensesStore, import.meta.hot));
}
